import type React from "react"
import { Package } from "lucide-react"
import type { MaterialUsed } from "./materialUsageUtils.ts"
import { materialDisplayLabel, materialOutcomeLabel } from "./materialUsageUtils.ts"

export type { MaterialUsed }

type MaterialsUsedCellProps = {
  materials?: MaterialUsed[] | null
  status?: string
  /** When set, show "None" instead of em dash for these statuses when materials are empty. */
  resolvedStatuses?: string[]
}

function outcomeHint(outcome?: string | null) {
  const label = materialOutcomeLabel(outcome)
  if (!label) return null
  return label.toLowerCase()
}

export function MaterialsUsedCell({
  materials,
  status,
  resolvedStatuses = ["completed", "resolved", "closed"],
}: MaterialsUsedCellProps) {
  const rows = materials ?? []
  const isResolved = resolvedStatuses.includes(String(status || ""))

  if (rows.length === 0) {
    if (!isResolved) {
      return <span className="text-xs text-slate-gray/50">—</span>
    }
    return <span className="text-xs text-slate-gray">None</span>
  }

  const tooltip = rows
    .map((row) => {
      const hint = outcomeHint(row.usage_outcome)
      const base = materialDisplayLabel(row)
      return hint ? `${base} (${hint})` : base
    })
    .join("\n")

  const visible = rows.slice(0, 2)
  const overflow = rows.length - visible.length

  return (
    <div className="min-w-[8rem] max-w-[14rem]" title={tooltip}>
      <div className="flex items-start gap-1.5">
        <Package className="mt-0.5 h-3.5 w-3.5 shrink-0 text-slate-gray/70" aria-hidden />
        <div className="min-w-0 space-y-0.5">
          {visible.map((row, index) => (
            <p key={`${row.item_type}-${index}`} className="truncate text-xs text-slate-gray">
              {materialDisplayLabel(row)}
            </p>
          ))}
          {overflow > 0 ? (
            <p className="text-[11px] font-medium text-electric-blue">+{overflow} more</p>
          ) : null}
        </div>
      </div>
    </div>
  )
}
